/* global __META_OPTIMIZER_ENABLED__ */
export const META_OPTIMIZER_ENABLED = typeof __META_OPTIMIZER_ENABLED__ !== 'undefined'
    && __META_OPTIMIZER_ENABLED__ === true;

import { securityIdentityPresentation } from './securityIdentityPresentation';

export const DESIGN_CONTRACT = 'optimizer-design-study.v1';
export const clone = (value) => JSON.parse(JSON.stringify(value));
export const GOALS = {
    min_factor_vol: 'Minimize factor volatility',
    max_idio_vol: 'Maximize idiosyncratic volatility · experimental',
    max_idio_share: 'Maximize idiosyncratic share · experimental',
    max_alpha: 'Increase conviction / Alpha-Score',
    min_turnover: 'Minimize trading',
};
export const METRICS = {
    gross_pct_aum: { label: 'Gross invested', unit: '% AUM', precision: 2 },
    net_pct_aum: { label: 'Net exposure', unit: '% AUM', precision: 2 },
    factor_vol_pct_aum: { label: 'Factor volatility', unit: '% AUM', precision: 2 },
    idio_vol_pct_aum: { label: 'Idiosyncratic volatility', unit: '% AUM', precision: 2 },
    idio_variance_share_pct: { label: 'Idiosyncratic share', unit: '% variance', precision: 2 },
    alpha_retention_pct: { label: 'Conviction retained', unit: '% baseline score', precision: 2 },
    alpha_score: { label: 'Alpha-Score', unit: '$ score', precision: 0 },
    turnover_pct_gmv: { label: 'Turnover', unit: '% GMV', precision: 2 },
    total_vol_pct_aum: { label: 'Total volatility', unit: '% AUM', precision: 2 },
    names_changed: { label: 'Changed names', unit: 'names', precision: 0 },
    largest_position_pct_aum: { label: 'Largest position', unit: '% AUM', precision: 2 },
    largest_idio_contribution_pct: { label: 'Largest idio contributor', unit: '% idio variance', precision: 2 },
    effective_idio_names: { label: 'Effective idio diversification', unit: 'names', precision: 1 },
    tracking_vol_pct_aum: { label: 'Deviation from sized ideas', unit: '% AUM specific tracking vol', precision: 2 },
};
export const numberText = (value, digits = 2) => value == null || !Number.isFinite(Number(value)) ? '—' : Number(value).toLocaleString(undefined, { maximumFractionDigits: digits, minimumFractionDigits: digits });

export function newDesignSettings() {
    return {
        label: 'Constraint search', intent: 'custom',
        design: {
            contract: 'optimizer-portfolio-design.v1', goal: 'max_alpha', construction_mode: 'resize', construction: null,
            preferences: { conviction: 1, risk: 0, turnover: 0, tracking: 0 },
            mandate: { score_floor: null, preserve_sides: false, preserve_gross: false, preserve_net: false, position_band_pct: null, total_vol_max_pct_aum: null, position_bands: [], factor_targets: [], baskets: [], hedge_sleeve: null },
        },
        search: [],
        trial_budget: 64, seed: 11, trial_seconds: 15, search_seconds: 600,
        compare_hedging: false, reinvest_headroom: false,
    };
}

export function requireStudy(value) {
    if (value?.contract !== DESIGN_CONTRACT || !Array.isArray(value?.trials) || !value?.settings?.design || !value.study_id) throw new Error('The study response was invalid.');
    return value;
}

export function publicCandidate(value) {
    if (!value || !Number.isInteger(value.index)) throw new Error('The portfolio response was invalid.');
    if (value.status === 'verified' && (!Array.isArray(value.candidate?.response?.result?.allocations) || !value.metrics)) throw new Error('This portfolio could not be loaded. Please try again.');
    return value;
}

export function designSecurityLabel(row, field = 'company_name') {
    const label = securityIdentityPresentation(row, field).value;
    if (label !== '—') return label;
    const fallback = field === 'company_name' ? securityIdentityPresentation(row, 'ticker').value : '—';
    return fallback === '—' ? 'Security label unavailable' : fallback;
}

export function designSecurityRows(baseline, securities = []) {
    const byId = new Map(securities.map((row) => [row.cognive_id, row]));
    return (baseline?.holdings ?? securities).map((row) => ({ ...byId.get(row.cognive_id), ...row,
        // The captured numeric inputs win, while customer labels come from the
        // same published Watchlist rows used by the shared portfolio table.
        msi_listing_label: byId.get(row.cognive_id)?.msi_listing_label ?? row.msi_listing_label,
        ticker: byId.get(row.cognive_id)?.ticker ?? row.ticker,
        company_name: byId.get(row.cognive_id)?.company_name ?? row.company_name,
        inbound_id_type: byId.get(row.cognive_id)?.inbound_id_type ?? row.inbound_id_type,
        inbound_id_value: byId.get(row.cognive_id)?.inbound_id_value ?? row.inbound_id_value,
    }));
}

export const designPublicMessage = (value) => String(value ?? '').replace(/\bcog[_-][a-z0-9_-]+\b/gi, '[security]');


export function searchPortfolioSelection({ study, candidate: inspectedCandidate, currentResult,
    currentRunOperationId, currentPortfolioLoaded, stage = null }) {
    const trials = study?.trials ?? [];
    // Only the exact recorded run establishes membership, never similar metrics.
    const loadedIndex = currentPortfolioLoaded ? trials.findIndex((trial) => (
        (currentResult?.studyId === study?.study_id && currentResult?.index === trial.index)
        || (currentRunOperationId != null && trial.run_operation_id === currentRunOperationId)
    )) : -1;
    const loadedTrial = loadedIndex < 0 ? null : { ...trials[loadedIndex], number: loadedIndex + 1 };
    const candidate = inspectedCandidate && inspectedCandidate.status !== 'verified' ? inspectedCandidate
        : loadedTrial?.index === inspectedCandidate?.index ? inspectedCandidate : loadedTrial;
    const candidateIndex = trials.findIndex((trial) => trial.index === candidate?.index);
    return {
        loadedTrial, candidate, currentPortfolioLoaded: Boolean(currentPortfolioLoaded),
        candidateNumber: candidateIndex < 0 ? null : candidateIndex + 1,
        selected: stage == null ? candidate : candidate?.stages?.[stage],
        isCurrent: loadedTrial != null && loadedTrial.index === candidate?.index && (currentResult?.stage ?? null) === stage,
    };
}
